import React from 'react'
import DatePicker from 'react-datepicker'
import {Control, Controller, FieldErrors, FieldValues, Path, RegisterOptions} from 'react-hook-form'

import 'react-datepicker/dist/react-datepicker.css'

type PickerType = 'date' | 'month' | 'year'

type DateInputProps<T extends FieldValues> = {
  name: Path<T>
  label?: string
  control: Control<T>
  errors?: FieldErrors<T>
  required?: boolean | string
  className?: string
  inputClassName?: string
  placeholder?: string
  minDate?: Date
  maxDate?: Date
  filterDate?: (date: Date) => boolean
  defaultValue?: string | Date | null
  onChange?: (value: string) => void
  pickerType?: PickerType
  options?: Omit<RegisterOptions<T, Path<T>>, 'setValueAs' | 'valueAsNumber' | 'valueAsDate'>
}

const getFieldError = (errors: FieldErrors<any> | undefined, name: string) => {
  return name.split('.').reduce<any>((obj, key) => obj?.[key], errors)
}

const toDate = (value: unknown): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }

  if (typeof value !== 'string' || value.trim() === '') {
    return null
  }

  const trimmed = value.trim()

  // YYYY-MM-DD
  const fullDateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed)

  if (fullDateMatch) {
    const year = Number(fullDateMatch[1])
    const month = Number(fullDateMatch[2]) - 1
    const day = Number(fullDateMatch[3])

    const parsed = new Date(year, month, day)

    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  const parsed = new Date(trimmed)

  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const toDateInputValue = (date: Date, pickerType: PickerType): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  switch (pickerType) {
    case 'month':
      return `${year}-${month}-01`

    case 'year':
      return `${year}-01-01`

    default:
      return `${year}-${month}-${day}`
  }
}

const formatRawDateInput = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 8)

  if (digits.length <= 2) {
    return digits
  }

  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`
  }

  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
}

const parseRawDateInput = (value: string): Date | null => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value)

  if (!match) {
    return null
  }

  const day = Number(match[1])
  const month = Number(match[2])
  const year = Number(match[3])
  const parsed = new Date(year, month - 1, day)

  return parsed.getFullYear() === year &&
    parsed.getMonth() === month - 1 &&
    parsed.getDate() === day
    ? parsed
    : null
}

const toDateOnlyTime = (date: Date): number => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

const isDateSelectable = (
  date: Date,
  minDate?: Date,
  maxDate?: Date,
  filterDate?: (date: Date) => boolean
): boolean => {
  const dateOnlyTime = toDateOnlyTime(date)

  if (minDate && dateOnlyTime < toDateOnlyTime(minDate)) {
    return false
  }

  if (maxDate && dateOnlyTime > toDateOnlyTime(maxDate)) {
    return false
  }

  return filterDate ? filterDate(date) : true
}

export const DateInput = <T extends FieldValues>({
  name,
  label,
  control,
  errors,
  required = false,
  className = '',
  inputClassName = '',
  placeholder,
  minDate,
  maxDate,
  filterDate,
  defaultValue = null,
  onChange,
  pickerType = 'date',
  options = {},
}: DateInputProps<T>) => {
  const error = getFieldError(errors, String(name))

  return (
    <div className={`mb-3 ${className}`}>
      {label && (
        <label htmlFor={String(name)} className='form-label fw-semibold'>
          {label}

          {required && <span className='text-danger ms-1'>*</span>}
        </label>
      )}

      <Controller
        name={name}
        control={control}
        defaultValue={(defaultValue ?? null) as T[Path<T>]}
        rules={{
          ...options,
          required,
        }}
        render={({field}) => {
          const selectedDate = toDate(field.value) ?? toDate(defaultValue)

          return (
            <DatePicker
              id={String(name)}
              selected={selectedDate}
              onChange={(date: Date | null) => {
                const nextValue = date ? toDateInputValue(date, pickerType) : null

                field.onChange(nextValue)
                onChange?.(nextValue ?? '')
              }}
              onBlur={field.onBlur}
              autoComplete='off'
              wrapperClassName='w-100'
              showPopperArrow={false}
              showYearDropdown
              dropdownMode='select'
              showMonthYearPicker={pickerType === 'month'}
              showYearPicker={pickerType === 'year'}
              minDate={minDate}
              maxDate={maxDate}
              filterDate={filterDate}
              strictParsing={pickerType === 'date'}
              dateFormat={
                pickerType === 'month' ? 'MMMM yyyy' : pickerType === 'year' ? 'yyyy' : 'dd/MM/yyyy'
              }
              placeholderText={
                placeholder ??
                (pickerType === 'month'
                  ? 'Select month'
                  : pickerType === 'year'
                  ? 'YYYY'
                  : 'DD/MM/YYYY')
              }
              className={`form-control ${inputClassName} ${error ? 'is-invalid' : ''}`}
              onChangeRaw={(event?: React.SyntheticEvent<HTMLElement>) => {
                if (pickerType !== 'date' || !event) {
                  return
                }

                const input = event.target

                if (!(input instanceof HTMLInputElement)) {
                  return
                }

                const formatted = formatRawDateInput(input.value)
                const parsedDate = parseRawDateInput(formatted)
                const nextValue =
                  formatted.length === 10 &&
                  parsedDate &&
                  isDateSelectable(parsedDate, minDate, maxDate, filterDate)
                    ? toDateInputValue(parsedDate, pickerType)
                    : null

                input.value = formatted

                if (!nextValue) {
                  field.onChange(null)
                  onChange?.('')
                }
              }}
            />
          )
        }}
      />

      {error && <div className='text-danger mt-1'>{error.message}</div>}
    </div>
  )
}
